import { ReactNode, useState } from "react";
import { useCustomersServiceObjectListQuery } from "@/services/backend/customers/api/service-object-api";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Button } from "@/shared/components/ui/button";
import { Check, ChevronsUpDown } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/shared/components/ui/command";
import { cn } from "@/shared/lib/utils";
import { NullUUID } from "@/shared/lib/utilities/uuid";
import t from "@/lang/lang";

export function ServiceObjectsSelect({
  customerId,
  value,
  onValueChange,
  note,
  disabled,
}: {
  customerId?: string;
  value?: string[];
  onValueChange: (value: string[]) => void;
  note?: ReactNode;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const { data: list } = useCustomersServiceObjectListQuery({
    customer: {
      active: customerId !== undefined,
      values: [customerId ?? NullUUID],
    },
  });
  const selection = value ?? [];

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="justify-between"
          disabled={disabled}
        >
          {selection.length > 0 ? (
            selection
              .map((id) => list?.serviceObjects.find((c) => c.id === id)?.name)
              .join(", ")
          ) : (
            <span className="text-muted-foreground font-normal">
              {t("Service Objekte auswählen ...")}
            </span>
          )}
          <ChevronsUpDown className="opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0">
        {note && (
          <div className="text-muted-foreground border-b p-2 text-xs">
            {note}
          </div>
        )}
        <Command>
          <CommandInput placeholder={t("Suchen")} className="h-9" />
          <CommandEmpty>{t("Nichts gefunden")}</CommandEmpty>
          <CommandGroup>
            {list?.serviceObjects.map((serviceObject) => (
              <CommandItem
                key={serviceObject.id}
                value={serviceObject.name}
                onSelect={() => {
                  if (selection.some((cId) => cId === serviceObject.id)) {
                    onValueChange(
                      selection.filter((cId) => cId !== serviceObject.id),
                    );
                  } else {
                    onValueChange([...selection, serviceObject.id]);
                  }
                  setOpen(false);
                }}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    selection.some((cId) => cId === serviceObject.id)
                      ? "opacity-100"
                      : "opacity-0",
                  )}
                />
                <span>{serviceObject.name}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
